import { useState } from 'react';
import { Alert, AlertTitle, Collapse } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';
import { en } from '../translations/en';
import { logger } from '../utils/logger';

const STORAGE_KEY = 'displayNameNoticeDismissed';

const readDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch (error) {
    logger.debug('Failed to read displayName notice dismissal from sessionStorage', error);
    return false;
  }
};

const writeDismissed = (): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, 'true');
  } catch (error) {
    // sessionStorage may be unavailable (private browsing, quota exceeded, iframe sandbox)
    logger.debug('Failed to write displayName notice dismissal to sessionStorage', error);
  }
};

export const DisplayNameNotice = () => {
  const { isAuthenticated } = useAuth();
  const { profile, displayName, loading, error } = useUserProfile();
  const [dismissed, setDismissed] = useState(readDismissed);

  // When profile fetch fails, hide the notice rather than risk showing
  // incorrect prompts. Profile errors are surfaced by other components.
  const shouldShow =
    isAuthenticated &&
    profile !== null &&
    !loading &&
    !error &&
    !displayName?.trim() &&
    !dismissed;

  const handleClose = () => {
    setDismissed(true);
    writeDismissed();
  };

  const t = en.displayNameNotice;

  return (
    <Collapse in={shouldShow}>
      <Alert severity="info" onClose={handleClose} sx={{ mb: 2 }}>
        <AlertTitle>{t.title}</AlertTitle>
        {t.message}{' '}
        <Link to="/settings">{t.linkText}</Link>{' '}
        {t.messageSuffix}
      </Alert>
    </Collapse>
  );
};
